<template>
    <!-- 管理员-项目管理/观思问配置 -->
    <div class="observe-config wrapper" v-loading="loading">
        <proTitle></proTitle>

        <div class="phase-list" v-if="phaseList.length">
            <div class="phase-card" :class="{ 'is-disabled': !phase.gswEnabled }" v-for="phase in phaseList"
                :key="phase.id">
                <div class="phase-card-head">
                    <div class="phase-card-head-left">
                        <i class="el-icon-location pin-icon"></i>
                        <span class="phase-title">{{ phase.displayName }}</span>
                        <span class="phase-status" :class="phase.gswEnabled ? 'enabled' : 'disabled'">
                            {{ phase.gswEnabled ? '前端已启用' : '前端未启用' }}
                        </span>
                    </div>
                    <div class="phase-card-actions">
                        <el-button size="mini" class="btn-toggle-enable"
                            :class="{ 'is-enabled': phase.gswEnabled }" :loading="phase._toggling"
                            @click="toggleFrontend(phase)">
                            {{ phase.gswEnabled ? '取消启用' : '启用' }}
                        </el-button>
                        <el-button size="mini" class="btn-gsw-config" @click="openQuestionDialog(phase)">编辑问题</el-button>
                        <el-button size="mini" class="btn-gsw-config" @click="openAnswerDialog(phase)">编辑作答日期</el-button>
                    </div>
                </div>

                <!-- 作答与总结日期摘要 -->
                <div class="gsw-schedule">
                    <p class="schedule-title">作答与总结日期</p>
                    <div class="schedule-lines" v-if="phase.periods.length">
                        <p class="schedule-line" v-for="(line, index) in scheduleLines(phase)" :key="index"
                            :class="{ 'is-muted': line.muted, 'is-empty': line.empty }">{{ line.text }}</p>
                    </div>
                    <p class="schedule-line is-empty" v-else>请先在阶段管理中配置周次</p>
                </div>

                <!-- 用户组tab：全部 + 本阶段适用用户组 -->
                <div class="group-tabs">
                    <span class="group-tab" :class="{ 'is-active': phase.activeGroup === 'all' }"
                        @click="phase.activeGroup = 'all'">全部</span>
                    <span class="group-tab" :class="{ 'is-active': phase.activeGroup === group.key }"
                        v-for="group in phase.groupOptions" :key="group.key"
                        @click="phase.activeGroup = group.key">
                        {{ group.label }}
                        <i class="group-tag" :class="isCustomGroup(phase, group.key) ? 'is-custom' : 'is-shared'">
                            {{ isCustomGroup(phase, group.key) ? '单独' : '共用' }}
                        </i>
                    </span>
                </div>

                <!-- 观思问题目预览 -->
                <div class="phase-blocks">
                    <div class="phase-block" v-for="item in questionBlocks(phase)" :key="item.type"
                        :class="item.type">
                        <p class="block-label">
                            <i :class="item.icon"></i>
                            <span>{{ item.label }}</span>
                        </p>
                        <p class="block-text" :title="item.text || ''">{{ item.text || '未配置' }}</p>
                    </div>
                </div>
            </div>
        </div>
        <el-empty v-else-if="!loading" description="暂无已启用的阶段，请先在「阶段管理」中启用阶段后，再配置观思问"></el-empty>

        <!-- 编辑问题 -->
        <el-dialog title="观思问配置 · 编辑问题" :visible.sync="questionDialogVisible" width="720px" top="6vh"
            :close-on-click-modal="false">
            <p class="dialog-subtitle" v-if="editingPhase">{{ editingPhase.displayName }}</p>
            <div class="group-tabs modal-group-tabs" v-if="editingPhase">
                <span class="group-tab" :class="{ 'is-active': editingGroup === 'all' }"
                    @click="switchEditingGroup('all')">全部</span>
                <span class="group-tab" :class="{ 'is-active': editingGroup === group.key }"
                    v-for="group in editingPhase.groupOptions" :key="group.key"
                    @click="switchEditingGroup(group.key)">
                    {{ group.label }}
                    <i class="group-tag"
                        :class="isCustomGroup(editingPhase, group.key) ? 'is-custom' : 'is-shared'">
                        {{ isCustomGroup(editingPhase, group.key) ? '单独' : '共用' }}
                    </i>
                </span>
            </div>
            <p class="form-hint">「全部」为共用题库：未单独修改的用户组共用同一套问题。在某一用户组中修改后，仅该组变更，其他组保持原样。勾选下方选项可一次覆盖所有用户组。</p>
            <el-checkbox v-model="syncAllGroups" v-show="editingGroup === 'all'" class="sync-checkbox">
                保存时同步到全部用户组（含已单独修改的）
            </el-checkbox>
            <el-form label-width="90px" size="small" @submit.native.prevent>
                <template v-for="item in questionTypes">
                    <el-form-item :label="item.name" :key="item.type + 'label'">
                        <el-input v-model.trim="questionForm[item.type + 'Label']" placeholder="标签名称"
                            style="width:200px"></el-input>
                    </el-form-item>
                    <el-form-item :label="item.name + '问题'" required :key="item.type + 'text'">
                        <el-input type="textarea" :rows="2" v-model.trim="questionForm[item.type + 'Text']"
                            :placeholder="`请输入${item.name}类详细问题`"></el-input>
                    </el-form-item>
                </template>
            </el-form>
            <span slot="footer">
                <el-button size="small" @click="questionDialogVisible = false">取消</el-button>
                <el-button size="small" type="primary" :loading="savingQuestions" @click="saveQuestions">保存</el-button>
            </span>
        </el-dialog>

        <!-- 编辑作答日期 -->
        <el-dialog title="观思问配置 · 编辑作答日期" :visible.sync="answerDialogVisible" width="720px" top="6vh"
            custom-class="observe-answer-dialog" :close-on-click-modal="false">
            <p class="dialog-subtitle" v-if="editingPhase">{{ editingPhase.displayName }}</p>
            <div class="section-title">观思问作答日期</div>
            <el-radio-group v-model="answerForm.mode" size="small" class="answer-mode-group">
                <el-radio label="fixedDates">固定日期</el-radio>
                <el-radio label="minDays">固定最小作答天数</el-radio>
            </el-radio-group>
            <div class="week-periods">
                <span class="week-periods-label">阶段周次（来自阶段编辑）</span>
                <div class="week-periods-text">{{ weekPeriodsText }}</div>
            </div>

            <div v-if="answerForm.mode === 'fixedDates'" class="answer-panel">
                <div class="gsw-answer-fixed-head">
                    <span class="form-hint" style="margin:0">在周次范围内点选或滑动拖选作答日期</span>
                    <div class="gsw-answer-actions">
                        <el-button type="text" size="small" @click="generateAnswerDates">根据周次生成</el-button>
                        <span class="gsw-answer-selected-count">已选 {{ answerForm.fixedDates.length }} 天</span>
                    </div>
                </div>
                <div class="gsw-answer-calendar gsw-week-calendar-mode" v-if="editingPeriods.length">
                    <div class="gsw-week-calendar" :class="{ 'is-dragging': calendarDrag.active }"
                        @mousedown="onAnswerCalendarMouseDown($event)"
                        @mouseover="onAnswerCalendarMouseOver($event)"
                        @mouseup="onAnswerCalendarMouseUp($event)">
                        <div class="gsw-week-block" v-for="(period, index) in editingPeriods" :key="index">
                            <div class="gsw-week-block-head">
                                <span class="gsw-week-block-label">{{ weekLabel(index) }}</span>
                                <span class="gsw-week-block-range">{{ period.start }} ~ {{ period.end }}</span>
                            </div>
                            <div class="gsw-week-days">
                                <button type="button" class="calendar-day gsw-week-day"
                                    v-for="cell in weekCalendarDays(period)" :key="cell.date" :class="{
                                        'is-selected': cell.selected,
                                        'is-in-range': cell.inRange,
                                        'is-deselect-preview': cell.deselectPreview,
                                        'is-summary': cell.isSummary,
                                    }" :data-date="cell.date" tabindex="-1">
                                    <span class="gsw-week-day-weekday">{{ cell.weekday }}</span>
                                    <span class="gsw-week-day-num">{{ cell.day }}</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="gsw-week-calendar-empty" v-else>请先在「编辑阶段」中配置阶段周次</div>
            </div>
            <div v-else class="answer-panel">
                <p class="form-hint">按阶段周次分别设置每周最少作答天数（不超过该周总天数）</p>
                <div class="gsw-min-week-list" v-if="answerForm.weekMinDays.length">
                    <div class="gsw-min-week-block" v-for="(week, index) in answerForm.weekMinDays" :key="index">
                        <div class="gsw-min-week-head">
                            <span class="gsw-min-week-label">{{ weekLabel(index) }}</span>
                            <span class="gsw-min-week-range">{{ editingPeriods[index].start }} ~
                                {{ editingPeriods[index].end }}</span>
                            <span class="gsw-min-week-days-count">共{{ weekDayCount(editingPeriods[index]) }}天</span>
                        </div>
                        <div class="gsw-min-week-row">
                            <span>最少作答</span>
                            <el-input-number v-model="week.value" :min="1"
                                :max="weekDayCount(editingPeriods[index])" size="mini"></el-input-number>
                            <span>天</span>
                        </div>
                    </div>
                </div>
                <div class="gsw-week-calendar-empty" v-else>请先在「编辑阶段」中配置阶段周次</div>
            </div>

            <div class="section-title summary-section-title">观思问总结</div>
            <el-radio-group v-model="answerForm.summaryMode" size="small" class="answer-mode-group">
                <el-radio label="weekly">周总结</el-radio>
                <el-radio label="multiWeek">多周总结</el-radio>
            </el-radio-group>
            <div v-if="answerForm.summaryMode === 'weekly'" class="answer-panel">
                <p class="form-hint">按每一周分别设置总结日期（选填，须在该周范围内）</p>
                <div class="gsw-summary-weekly-list" v-if="answerForm.weekSummaryDates.length">
                    <div class="gsw-summary-week-item" v-for="(item, index) in answerForm.weekSummaryDates"
                        :key="index">
                        <div class="gsw-min-week-head">
                            <span class="gsw-min-week-label">{{ weekLabel(index) }}</span>
                            <span class="gsw-min-week-range">{{ editingPeriods[index].start }} ~
                                {{ editingPeriods[index].end }}</span>
                        </div>
                        <div class="gsw-week-summary-row">
                            <span>总结日期</span>
                            <el-date-picker v-model="item.date" type="date" value-format="yyyy-MM-dd" size="mini"
                                placeholder="总结日期（选填）" :picker-options="weekPickerOptions(editingPeriods[index])"
                                style="width:170px" clearable></el-date-picker>
                        </div>
                    </div>
                </div>
                <div class="gsw-week-calendar-empty" v-else>请先在「编辑阶段」中配置阶段周次</div>
            </div>
            <div v-else class="answer-panel">
                <div class="gsw-answer-fixed-head" style="margin-bottom:8px">
                    <span class="form-hint" style="margin:0">每组多周选择一个总结日期（选填，须在所选周次范围内）</span>
                    <el-button type="text" size="small" @click="addMultiSummary">添加多周总结</el-button>
                </div>
                <div class="gsw-multi-summary-empty" v-if="!answerForm.multiWeekSummaries.length">
                    点击「添加多周总结」创建合并总结</div>
                <div class="gsw-multi-summary-list" v-else>
                    <div class="gsw-summary-week-item gsw-multi-summary-item"
                        v-for="(item, index) in answerForm.multiWeekSummaries" :key="index">
                        <div class="gsw-min-week-head">
                            <span class="gsw-min-week-label">{{ multiWeekLabel(item) }}</span>
                            <span class="gsw-min-week-range">{{ multiWeekRangeText(item) }}</span>
                            <div class="gsw-multi-range-picks">
                                <el-select v-model="item.startWeek" size="mini" style="width:92px"
                                    @change="onMultiWeekRangeChange(item)">
                                    <el-option v-for="(period, i) in editingPeriods" :key="i" :label="weekLabel(i)"
                                        :value="i + 1"></el-option>
                                </el-select>
                                <span class="gsw-multi-summary-sep">至</span>
                                <el-select v-model="item.endWeek" size="mini" style="width:92px"
                                    @change="onMultiWeekRangeChange(item)">
                                    <el-option v-for="(period, i) in editingPeriods" :key="i" :label="weekLabel(i)"
                                        :value="i + 1" :disabled="i + 1 < item.startWeek"></el-option>
                                </el-select>
                            </div>
                            <el-button type="text" class="btn-delete-summary"
                                @click="answerForm.multiWeekSummaries.splice(index, 1)">删除</el-button>
                        </div>
                        <div class="gsw-week-summary-row">
                            <span>总结日期</span>
                            <el-date-picker v-model="item.date" type="date" value-format="yyyy-MM-dd" size="mini"
                                placeholder="总结日期（选填）" :picker-options="multiWeekPickerOptions(item)"
                                style="width:170px" clearable></el-date-picker>
                        </div>
                    </div>
                </div>
            </div>
            <span slot="footer">
                <el-button size="small" @click="answerDialogVisible = false">取消</el-button>
                <el-button size="small" type="primary" :loading="savingAnswer" @click="saveAnswerConfig">保存</el-button>
            </span>
        </el-dialog>
    </div>
</template>

<script>
    import { mapState } from 'vuex';

    // 将日期数组中连续的日期合并为范围显示，格式 yyyy.MM.dd-yyyy.MM.dd，单独的单独显示
    const formatDateRanges = (dates) => {
        if (!dates.length) return '';
        let sorted = dates.slice().sort();
        let ranges = [];
        let rangeStart = sorted[0];
        let prev = sorted[0];
        for (let i = 1; i < sorted.length; i++) {
            let prevDate = new Date(prev.replace(/-/g, '/'));
            let currDate = new Date(sorted[i].replace(/-/g, '/'));
            if (currDate - prevDate === 86400000) {
                prev = sorted[i];
            } else {
                ranges.push([rangeStart, prev]);
                rangeStart = sorted[i];
                prev = sorted[i];
            }
        }
        ranges.push([rangeStart, prev]);
        let toDot = (d) => d.replace(/-/g, '.');
        return ranges.map(([s, e]) => s === e ? toDot(s) : `${toDot(s)}-${toDot(e)}`).join('、');
    };

    const datesInRange = (start, end) => {
        let dates = [];
        let current = new Date(start.replace(/-/g, '/'));
        let last = new Date(end.replace(/-/g, '/'));
        while (current <= last) {
            let m = String(current.getMonth() + 1).padStart(2, '0');
            let d = String(current.getDate()).padStart(2, '0');
            dates.push(`${current.getFullYear()}-${m}-${d}`);
            current.setDate(current.getDate() + 1);
        }
        return dates;
    };

    const dateToStr = (date) => {
        let m = String(date.getMonth() + 1).padStart(2, '0');
        let d = String(date.getDate()).padStart(2, '0');
        return `${date.getFullYear()}-${m}-${d}`;
    };

    const emptyQuestions = () => ({
        observeLabel: '观',
        observeText: '',
        reflectLabel: '思',
        reflectText: '',
        questionLabel: '问',
        questionText: '',
    });

    const unwrapApi = (res) => {
        if (!res) return res;
        if (res.status != null && res.data && typeof res.data === 'object' && 'code' in res.data) {
            return res.data;
        }
        return res;
    };

    const isApiSuccess = (res) => {
        let body = unwrapApi(res);
        return body && (body.code === 0 || body.code === 200);
    };

    const apiMsg = (res) => {
        let body = unwrapApi(res);
        return (body && body.msg) || '';
    };

    export default {
        data() {
            return {
                loading: false,
                savingQuestions: false,
                savingAnswer: false,
                questionTypes: [
                    { type: 'observe', name: '观察', icon: 'el-icon-view' },
                    { type: 'reflect', name: '思考', icon: 'el-icon-chat-round' },
                    { type: 'question', name: '提问', icon: 'el-icon-question' },
                ],
                phaseList: [],
                // 编辑问题弹窗
                questionDialogVisible: false,
                editingPhase: null,
                editingGroup: 'all',
                syncAllGroups: false,
                questionForm: emptyQuestions(),
                // 编辑作答日期弹窗
                answerDialogVisible: false,
                answerForm: {
                    mode: 'fixedDates',
                    fixedDates: [],
                    weekMinDays: [],
                    summaryMode: 'weekly',
                    weekSummaryDates: [],
                    multiWeekSummaries: [],
                },
                weekdayLabels: ['日', '一', '二', '三', '四', '五', '六'],
                calendarDrag: {
                    active: false,
                    moved: false,
                    mode: 'select',
                    anchor: null,
                    lastTarget: null,
                },
                calendarDragPreview: [],
            };
        },
        computed: {
            ...mapState({
                currentProject: (state) => state.user.currentProject,
            }),
            projectId() {
                return this.currentProject && this.currentProject.id;
            },
            editingPeriods() {
                return this.editingPhase ? this.editingPhase.periods : [];
            },
            weekPeriodsText() {
                if (!this.editingPeriods.length) return '请先在「编辑阶段」中配置阶段周次';
                let total = this.editingPeriods.reduce((sum, period) => sum + this.weekDayCount(period), 0);
                return `共${this.editingPeriods.length}周：` + this.editingPeriods
                    .map((period, i) => `${this.weekLabel(i)} ${period.start} ~ ${period.end}`)
                    .join('；') + `（共${total}天）`;
            },
            summaryHighlightSet() {
                let set = {};
                let form = this.answerForm;
                if (form.summaryMode === 'weekly') {
                    (form.weekSummaryDates || []).forEach(item => {
                        if (item && item.date) set[item.date] = true;
                    });
                } else {
                    (form.multiWeekSummaries || []).forEach(item => {
                        if (item && item.date) set[item.date] = true;
                    });
                }
                return set;
            },
        },
        watch: {
            projectId: {
                immediate: true,
                handler(id) {
                    if (id) this.loadAll();
                    else this.phaseList = [];
                },
            },
        },
        mounted() {
            this._onDocMouseUp = () => this.endAnswerCalendarDrag();
            document.addEventListener('mouseup', this._onDocMouseUp);
        },
        beforeDestroy() {
            document.removeEventListener('mouseup', this._onDocMouseUp);
        },
        methods: {
            weekLabel(index) {
                const cn = ['一', '二', '三', '四', '五', '六', '七', '八', '九', '十'];
                return `第${cn[index] || index + 1}周`;
            },
            weekDayCount(period) {
                return datesInRange(period.start, period.end).length;
            },
            weekdayLabel(dateStr) {
                let parts = dateStr.split('-');
                let d = new Date(+parts[0], +parts[1] - 1, +parts[2]);
                return this.weekdayLabels[d.getDay()];
            },
            weekCalendarDays(period) {
                let selectedSet = {};
                (this.answerForm.fixedDates || []).forEach(date => {
                    selectedSet[date] = true;
                });
                let previewSet = {};
                this.calendarDragPreview.forEach(date => {
                    previewSet[date] = true;
                });
                let isDeselectPreview = this.calendarDrag.active && this.calendarDrag.mode === 'deselect';
                return datesInRange(period.start, period.end).map(date => {
                    let selected = !!selectedSet[date];
                    let inPreview = !!previewSet[date];
                    return {
                        date,
                        day: Number(date.split('-')[2]),
                        weekday: this.weekdayLabel(date),
                        selected: selected && !(isDeselectPreview && inPreview),
                        inRange: !selected && !isDeselectPreview && inPreview,
                        deselectPreview: selected && isDeselectPreview && inPreview,
                        isSummary: !!this.summaryHighlightSet[date],
                    };
                });
            },
            isAnswerDateSelectable(dateStr) {
                return this.editingPeriods.some(period => dateStr >= period.start && dateStr <= period.end);
            },
            getAnswerDatesBetween(dateA, dateB) {
                if (!dateA || !dateB) return [];
                let start = dateA <= dateB ? dateA : dateB;
                let end = dateA <= dateB ? dateB : dateA;
                return datesInRange(start, end).filter(date => this.isAnswerDateSelectable(date));
            },
            setAnswerDragPreview(anchor, target) {
                this.calendarDragPreview = this.getAnswerDatesBetween(anchor, target);
            },
            applyAnswerDragRange(anchor, target, mode) {
                let dates = this.getAnswerDatesBetween(anchor, target);
                if (!dates.length) return;
                let selectedSet = {};
                (this.answerForm.fixedDates || []).forEach(date => {
                    selectedSet[date] = true;
                });
                if (mode === 'deselect') {
                    dates.forEach(date => {
                        delete selectedSet[date];
                    });
                } else {
                    dates.forEach(date => {
                        selectedSet[date] = true;
                    });
                }
                this.answerForm.fixedDates = Object.keys(selectedSet).sort();
            },
            toggleAnswerDate(date) {
                if (!this.isAnswerDateSelectable(date)) return;
                let dates = this.answerForm.fixedDates || [];
                let idx = dates.indexOf(date);
                if (idx === -1) dates.push(date);
                else dates.splice(idx, 1);
                this.answerForm.fixedDates = dates.slice().sort();
            },
            resetAnswerCalendarDrag() {
                this.calendarDrag = {
                    active: false,
                    moved: false,
                    mode: 'select',
                    anchor: null,
                    lastTarget: null,
                };
                this.calendarDragPreview = [];
            },
            endAnswerCalendarDrag() {
                let drag = this.calendarDrag;
                if (!drag.active) return;
                if (drag.moved && drag.anchor && drag.lastTarget) {
                    this.applyAnswerDragRange(drag.anchor, drag.lastTarget, drag.mode);
                }
                this.resetAnswerCalendarDrag();
            },
            onAnswerCalendarMouseDown(e) {
                if (e.button !== 0) return;
                let target = e.target && e.target.nodeType === 1 ? e.target : (e.target && e.target.parentElement);
                let btn = target && target.closest && target.closest('.calendar-day[data-date]');
                if (!btn || btn.disabled) return;
                e.preventDefault();
                let date = btn.getAttribute('data-date');
                if (!date) return;
                let isSelected = (this.answerForm.fixedDates || []).indexOf(date) !== -1;
                this.calendarDrag = {
                    active: true,
                    moved: false,
                    mode: isSelected ? 'deselect' : 'select',
                    anchor: date,
                    lastTarget: date,
                };
                this.setAnswerDragPreview(date, date);
            },
            onAnswerCalendarMouseOver(e) {
                let drag = this.calendarDrag;
                if (!drag.active || !drag.anchor) return;
                let target = e.target && e.target.nodeType === 1 ? e.target : (e.target && e.target.parentElement);
                let btn = target && target.closest && target.closest('.calendar-day[data-date]');
                if (!btn || btn.disabled) return;
                let date = btn.getAttribute('data-date');
                if (!date) return;
                if (date !== drag.anchor) drag.moved = true;
                drag.lastTarget = date;
                this.setAnswerDragPreview(drag.anchor, date);
            },
            onAnswerCalendarMouseUp(e) {
                let drag = this.calendarDrag;
                if (!drag.active || !drag.anchor) return;
                let target = e.target && e.target.nodeType === 1 ? e.target : (e.target && e.target.parentElement);
                let btn = target && target.closest && target.closest('.calendar-day[data-date]');
                let targetDate = btn && !btn.disabled ? btn.getAttribute('data-date') : drag.anchor;
                if (drag.moved) {
                    this.applyAnswerDragRange(drag.anchor, targetDate, drag.mode);
                } else if (btn && !btn.disabled && targetDate) {
                    this.toggleAnswerDate(targetDate);
                }
                this.resetAnswerCalendarDrag();
            },
            multiWeekLabel(item) {
                if (item.startWeek === item.endWeek) return this.weekLabel(item.startWeek - 1);
                return `${this.weekLabel(item.startWeek - 1)} ~ ${this.weekLabel(item.endWeek - 1)}`;
            },
            multiWeekRangeText(item) {
                let startIdx = (item.startWeek || 1) - 1;
                let endIdx = (item.endWeek || item.startWeek || 1) - 1;
                let start = this.editingPeriods[startIdx];
                let end = this.editingPeriods[endIdx];
                if (!start || !end) return '—';
                return `${start.start} ~ ${end.end}`;
            },
            weekPickerOptions(period) {
                if (!period) {
                    return { disabledDate() { return true; } };
                }
                return {
                    disabledDate(date) {
                        let str = dateToStr(date);
                        return str < period.start || str > period.end;
                    },
                };
            },
            multiWeekAllowedDates(item) {
                let allowed = {};
                let startIdx = (item.startWeek || 1) - 1;
                let endIdx = (item.endWeek || item.startWeek || 1) - 1;
                for (let i = startIdx; i <= endIdx; i++) {
                    let period = this.editingPeriods[i];
                    if (!period) continue;
                    datesInRange(period.start, period.end).forEach(date => (allowed[date] = true));
                }
                return allowed;
            },
            multiWeekPickerOptions(item) {
                let allowed = this.multiWeekAllowedDates(item);
                return {
                    disabledDate(date) {
                        return !allowed[dateToStr(date)];
                    },
                };
            },
            onMultiWeekRangeChange(item) {
                if (item.endWeek < item.startWeek) item.endWeek = item.startWeek;
                if (!item.date) return;
                let allowed = this.multiWeekAllowedDates(item);
                if (!allowed[item.date]) item.date = '';
            },
            isCustomGroup(phase, groupKey) {
                return (phase.customGroups || []).map(String).indexOf(String(groupKey)) !== -1;
            },
            normalizeQuestions(q) {
                let src = q || {};
                return {
                    observeLabel: src.observeLabel || '观',
                    observeText: src.observeText || '',
                    reflectLabel: src.reflectLabel || '思',
                    reflectText: src.reflectText || '',
                    questionLabel: src.questionLabel || '问',
                    questionText: src.questionText || '',
                };
            },
            mapPeriodsFromStage(stageVo) {
                return (stageVo.periods || []).map(p => ({
                    start: p.startDate,
                    end: p.endDate,
                }));
            },
            mapGswItem(vo, stageMap) {
                let stageId = vo.stageId;
                let stage = stageMap[stageId] || {};
                let periods = Array.isArray(vo.periods) && vo.periods.length
                    ? vo.periods.map(p => ({ start: p.startDate || p.start, end: p.endDate || p.end }))
                    : this.mapPeriodsFromStage(stage);

                let groupOptions = (vo.stageUserGroups || []).map(g => ({
                    key: String(g.id),
                    label: g.name,
                }));

                let questionsByGroup = vo.questionsByGroup || {};
                let groups = { all: this.normalizeQuestions(questionsByGroup.all) };
                groupOptions.forEach(g => {
                    groups[g.key] = this.normalizeQuestions(questionsByGroup[g.key] || questionsByGroup.all);
                });

                let summaryJson = vo.summaryJson || {};
                let weekSummaryDates = Array.isArray(summaryJson.weekSummaryDates)
                    ? summaryJson.weekSummaryDates.map(d => d || '')
                    : [];
                let multiWeekSummaries = Array.isArray(summaryJson.multiWeekSummaries)
                    ? summaryJson.multiWeekSummaries.map(item => ({
                        startWeek: item.startWeek,
                        endWeek: item.endWeek,
                        date: item.date || '',
                    }))
                    : [];

                return {
                    id: stageId,
                    projectId: vo.projectId,
                    displayName: vo.stageName || stage.stageName || '',
                    gswEnabled: Number(vo.frontendEnabled) === 1,
                    activeGroup: 'all',
                    customGroups: (vo.customGroups || []).map(String),
                    groupOptions,
                    periods,
                    groups,
                    answerConfig: {
                        mode: vo.answerMode || 'fixedDates',
                        fixedDates: (vo.answerDates || []).slice(),
                        weekMinDays: (vo.weekMinDays || []).slice(),
                        summaryMode: vo.summaryMode || 'weekly',
                        weekSummaryDates,
                        multiWeekSummaries,
                    },
                };
            },
            async loadAll() {
                if (!this.projectId) return;
                this.loading = true;
                try {
                    await this.fetchList();
                } finally {
                    this.loading = false;
                }
            },
            async fetchList() {
                let [gswRes, stageRes] = await Promise.all([
                    this.$Api.StageV2.getGswList({ projectId: this.projectId }),
                    this.$Api.StageV2.getStageList({ projectId: this.projectId }),
                ]);
                if (!isApiSuccess(gswRes)) {
                    this.$message.error(apiMsg(gswRes) || '获取观思问配置失败');
                    this.phaseList = [];
                    return;
                }
                let stageMap = {};
                if (isApiSuccess(stageRes)) {
                    (stageRes.data || []).forEach(s => {
                        stageMap[s.id] = s;
                    });
                }
                this.phaseList = (gswRes.data || []).map(item => this.mapGswItem(item, stageMap));
            },
            questionBlocks(phase) {
                let questions = phase.groups[phase.activeGroup] || phase.groups.all;
                return this.questionTypes.map(item => ({
                    type: item.type,
                    icon: item.icon,
                    label: questions[item.type + 'Label'] || item.name,
                    text: questions[item.type + 'Text'],
                }));
            },
            scheduleLines(phase) {
                let config = phase.answerConfig;
                let lines = [];
                phase.periods.forEach((period, index) => {
                    if (config.mode === 'fixedDates') {
                        let weekDates = datesInRange(period.start, period.end);
                        let selected = config.fixedDates.filter(date => weekDates.indexOf(date) !== -1);
                        lines.push({
                            text: `${this.weekLabel(index)}作答：${selected.length ? formatDateRanges(selected) : '未配置'}`,
                            empty: !selected.length,
                        });
                    } else {
                        lines.push({ text: `${this.weekLabel(index)}作答：最少${config.weekMinDays[index] || 1}天` });
                    }
                });
                if (config.summaryMode === 'multiWeek') {
                    if (!config.multiWeekSummaries.length) {
                        lines.push({ text: '总结：未配置', muted: true, empty: true });
                    } else {
                        config.multiWeekSummaries.forEach(item => {
                            let label = item.startWeek === item.endWeek
                                ? this.weekLabel(item.startWeek - 1)
                                : `${this.weekLabel(item.startWeek - 1)} ~ ${this.weekLabel(item.endWeek - 1)}`;
                            lines.push({ text: `${label}总结：${item.date || '未配置'}`, muted: true, empty: !item.date });
                        });
                    }
                } else {
                    phase.periods.forEach((period, index) => {
                        let date = config.weekSummaryDates[index] || '';
                        lines.push({ text: `${this.weekLabel(index)}总结：${date || '未配置'}`, muted: true, empty: !date });
                    });
                }
                return lines;
            },
            hasQuestionsConfigured(phase) {
                let q = (phase.groups && phase.groups.all) || {};
                return !!(q.observeText && q.reflectText && q.questionText);
            },
            hasAnswerConfigured(phase) {
                if (!phase.periods || !phase.periods.length) return false;
                let config = phase.answerConfig || {};
                if (config.mode === 'minDays') {
                    return Array.isArray(config.weekMinDays) && config.weekMinDays.length === phase.periods.length;
                }
                return Array.isArray(config.fixedDates) && config.fixedDates.length > 0;
            },
            async toggleFrontend(phase) {
                let next = phase.gswEnabled ? 0 : 1;
                if (next === 1) {
                    let missing = [];
                    if (!this.hasQuestionsConfigured(phase)) missing.push('问题');
                    if (!this.hasAnswerConfigured(phase)) missing.push('作答日期');
                    if (missing.length) {
                        this.$message.warning(`请先编辑${missing.join('与')}后再启用`);
                        return;
                    }
                }
                this.$set(phase, '_toggling', true);
                try {
                    let res = await this.$Api.StageV2.setGswFrontendEnabled({
                        stageId: phase.id,
                        enabled: next,
                    });
                    if (isApiSuccess(res)) {
                        phase.gswEnabled = next === 1;
                        this.$message.success(next === 1 ? '前端已启用' : '前端已取消启用');
                    } else {
                        this.$message.error(apiMsg(res) || '操作失败');
                    }
                } catch (e) {
                    this.$message.error('操作失败');
                } finally {
                    this.$set(phase, '_toggling', false);
                }
            },
            // ============ 编辑问题 ============
            async openQuestionDialog(phase) {
                this.editingPhase = phase;
                this.editingGroup = 'all';
                this.syncAllGroups = false;
                try {
                    let res = await this.$Api.StageV2.getGswDetail({ stageId: phase.id });
                    if (isApiSuccess(res)) {
                        let mapped = this.mapGswItem(res.data || {}, { [phase.id]: { periods: [], stageName: phase.displayName } });
                        mapped.periods = phase.periods;
                        mapped.groupOptions = mapped.groupOptions.length ? mapped.groupOptions : phase.groupOptions;
                        this.editingPhase = mapped;
                        // 同步列表卡片数据
                        let idx = this.phaseList.findIndex(p => p.id === phase.id);
                        if (idx !== -1) {
                            this.$set(this.phaseList, idx, {
                                ...mapped,
                                activeGroup: this.phaseList[idx].activeGroup || 'all',
                            });
                            this.editingPhase = this.phaseList[idx];
                        }
                    }
                } catch (e) { /* 用列表缓存即可 */ }
                this.fillQuestionForm();
                this.questionDialogVisible = true;
            },
            fillQuestionForm() {
                let questions = (this.editingPhase.groups[this.editingGroup]
                    || this.editingPhase.groups.all
                    || emptyQuestions());
                this.questionForm = { ...questions };
            },
            switchEditingGroup(groupKey) {
                if (this.editingGroup === groupKey) return;
                this.editingGroup = groupKey;
                this.fillQuestionForm();
            },
            async saveQuestions() {
                let form = this.questionForm;
                if (!form.observeText || !form.reflectText || !form.questionText) {
                    this.$message.warning(this.editingGroup === 'all'
                        ? '保存「全部」时请完整填写观察、思考、提问三类问题'
                        : '请完整填写观察、思考、提问三类问题');
                    return;
                }
                if (!this.projectId || !this.editingPhase) return;
                let questionsPayload = {};
                questionsPayload[this.editingGroup] = {
                    observeLabel: form.observeLabel,
                    observeText: form.observeText,
                    reflectLabel: form.reflectLabel,
                    reflectText: form.reflectText,
                    questionLabel: form.questionLabel,
                    questionText: form.questionText,
                };
                this.savingQuestions = true;
                try {
                    let res = await this.$Api.StageV2.saveGsw({
                        projectId: this.projectId,
                        stageId: this.editingPhase.id,
                        saveMode: 'questions',
                        editingGroup: this.editingGroup,
                        syncToAll: this.editingGroup === 'all' ? this.syncAllGroups : false,
                        frontendEnabled: this.editingPhase.gswEnabled ? 1 : 0,
                        questionsByGroup: questionsPayload,
                    });
                    if (isApiSuccess(res)) {
                        this.$message.success('保存成功');
                        this.questionDialogVisible = false;
                        await this.fetchList();
                    } else {
                        this.$message.error(apiMsg(res) || '保存失败');
                    }
                } catch (e) {
                    this.$message.error('保存失败');
                } finally {
                    this.savingQuestions = false;
                }
            },
            // ============ 编辑作答日期 ============
            async openAnswerDialog(phase) {
                this.editingPhase = phase;
                try {
                    let res = await this.$Api.StageV2.getGswDetail({ stageId: phase.id });
                    if (isApiSuccess(res)) {
                        let mapped = this.mapGswItem(res.data || {}, {});
                        mapped.periods = mapped.periods.length ? mapped.periods : phase.periods;
                        mapped.groupOptions = mapped.groupOptions.length ? mapped.groupOptions : phase.groupOptions;
                        let idx = this.phaseList.findIndex(p => p.id === phase.id);
                        if (idx !== -1) {
                            this.$set(this.phaseList, idx, {
                                ...mapped,
                                activeGroup: this.phaseList[idx].activeGroup || 'all',
                            });
                            this.editingPhase = this.phaseList[idx];
                        } else {
                            this.editingPhase = mapped;
                        }
                    }
                } catch (e) { /* 用列表缓存即可 */ }

                let config = this.editingPhase.answerConfig;
                let periods = this.editingPhase.periods;
                this.answerForm = {
                    mode: config.mode || 'fixedDates',
                    fixedDates: (config.fixedDates || []).slice(),
                    weekMinDays: periods.map((period, index) => ({
                        value: Math.min(config.weekMinDays[index] || 1, this.weekDayCount(period)),
                    })),
                    summaryMode: config.summaryMode || 'weekly',
                    weekSummaryDates: periods.map((period, index) => ({
                        date: config.weekSummaryDates[index] || '',
                    })),
                    multiWeekSummaries: (config.multiWeekSummaries || []).map(item => ({ ...item })),
                };
                this.resetAnswerCalendarDrag();
                this.answerDialogVisible = true;
            },
            generateAnswerDates() {
                let dateMap = {};
                this.editingPeriods.forEach(period => {
                    datesInRange(period.start, period.end).forEach(date => (dateMap[date] = true));
                });
                this.answerForm.fixedDates = Object.keys(dateMap).sort();
            },
            addMultiSummary() {
                this.answerForm.multiWeekSummaries.push({
                    startWeek: 1,
                    endWeek: Math.min(2, this.editingPeriods.length),
                    date: '',
                });
            },
            async saveAnswerConfig() {
                if (!this.editingPeriods.length) {
                    this.$message.warning('请先在「阶段管理」中配置阶段周次');
                    return;
                }
                let form = this.answerForm;
                if (form.mode === 'fixedDates' && !form.fixedDates.length) {
                    this.$message.warning('请至少选择一个固定作答日期（须在阶段周次范围内）');
                    return;
                }
                for (let i = 0; i < form.multiWeekSummaries.length; i++) {
                    let item = form.multiWeekSummaries[i];
                    if (item.endWeek < item.startWeek) item.endWeek = item.startWeek;
                }
                let summaryJson = form.summaryMode === 'weekly'
                    ? {
                        weekSummaryDates: form.weekSummaryDates.map(item => item.date || null),
                    }
                    : {
                        multiWeekSummaries: form.multiWeekSummaries.map(item => ({
                            startWeek: item.startWeek,
                            endWeek: item.endWeek,
                            date: item.date || null,
                        })),
                    };
                this.savingAnswer = true;
                try {
                    let res = await this.$Api.StageV2.saveGsw({
                        projectId: this.projectId,
                        stageId: this.editingPhase.id,
                        saveMode: 'answer',
                        answerMode: form.mode,
                        answerDates: form.mode === 'fixedDates' ? form.fixedDates.slice().sort() : [],
                        weekMinDays: form.mode === 'minDays' ? form.weekMinDays.map(item => item.value) : [],
                        summaryMode: form.summaryMode,
                        summaryJson,
                    });
                    if (isApiSuccess(res)) {
                        this.$message.success('保存成功');
                        this.answerDialogVisible = false;
                        await this.fetchList();
                    } else {
                        this.$message.error(apiMsg(res) || '保存失败');
                    }
                } catch (e) {
                    this.$message.error('保存失败');
                } finally {
                    this.savingAnswer = false;
                }
            },
        },
    };
</script>

<style lang="less" scoped>
    .observe-config {
        padding: 20px 25px;
        box-sizing: border-box;

        .phase-list {
            display: flex;
            flex-direction: column;
            gap: 16px;
        }

        .phase-card {
            background: #fff;
            border: 1px solid #93C5FD;
            border-radius: 10px;
            padding: 16px 18px;
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);

            &.is-disabled {
                opacity: 0.72;
                border-color: #D1D5DB;
                background: #FAFAFA;
            }
        }

        .phase-card-head {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 12px;
            margin-bottom: 10px;
            flex-wrap: wrap;
        }

        .phase-card-head-left {
            display: flex;
            align-items: center;
            gap: 8px;
            min-width: 0;

            .pin-icon {
                color: #EC4899;
                flex-shrink: 0;
            }

            .phase-title {
                font-size: 15px;
                font-weight: 600;
                color: #303133;
                white-space: nowrap;
            }
        }

        .phase-status {
            display: inline-flex;
            align-items: center;
            padding: 2px 8px;
            border-radius: 4px;
            font-size: 12px;
            font-weight: 500;
            white-space: nowrap;

            &.enabled {
                background: #DBEAFE;
                color: #2563EB;
            }

            &.disabled {
                background: #F3F4F6;
                color: #6B7280;
            }
        }

        .phase-card-actions {
            display: flex;
            align-items: center;
            gap: 8px;
            flex-shrink: 0;
        }

        .btn-toggle-enable {
            border-color: #86EFAC !important;
            background: #F0FDF4 !important;
            color: #16A34A !important;

            &:hover,
            &:focus {
                background: #DCFCE7 !important;
                border-color: #4ADE80 !important;
                color: #16A34A !important;
            }

            &.is-enabled {
                border-color: #DCDFE6 !important;
                background: #fff !important;
                color: #606266 !important;

                &:hover,
                &:focus {
                    color: #D97706 !important;
                    border-color: #FBBF24 !important;
                    background: #FFFBEB !important;
                }
            }
        }

        .btn-gsw-config {
            color: #409EFF !important;
            border-color: #B3D8FF !important;
            background: #ECF5FF !important;

            &:hover,
            &:focus {
                color: #409EFF !important;
                border-color: #409EFF !important;
                background: #D9ECFF !important;
            }
        }

        .gsw-schedule {
            margin-top: 10px;
            padding: 10px 12px;
            background: #F8FAFC;
            border: 1px solid #E2E8F0;
            border-radius: 8px;

            .schedule-title {
                font-size: 13px;
                font-weight: 600;
                color: #303133;
                margin-bottom: 6px;
            }

            .schedule-lines {
                display: flex;
                flex-direction: column;
                gap: 4px;
            }

            .schedule-line {
                font-size: 13px;
                color: #606266;
                line-height: 1.5;

                &.is-muted {
                    color: #909399;
                }

                &.is-empty {
                    color: #C0C4CC;
                }
            }
        }

        .phase-blocks {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 12px;
            margin-top: 10px;

            @media (max-width: 900px) {
                grid-template-columns: 1fr;
            }

            .phase-block {
                border-radius: 8px;
                padding: 12px 14px;
                min-height: 72px;

                &.observe {
                    background: #EFF6FF;

                    .block-label {
                        color: #2563EB;
                    }
                }

                &.reflect {
                    background: #FFFBEB;

                    .block-label {
                        color: #D97706;
                    }
                }

                &.question {
                    background: #ECFDF5;

                    .block-label {
                        color: #059669;
                    }
                }

                .block-label {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    font-size: 13px;
                    font-weight: 600;
                    margin-bottom: 6px;
                }

                .block-text {
                    font-size: 13px;
                    color: #606266;
                    line-height: 1.5;
                    display: -webkit-box;
                    -webkit-box-orient: vertical;
                    -webkit-line-clamp: 3;
                    overflow: hidden;
                    word-break: break-word;
                }
            }
        }
    }

    .group-tabs {
        display: flex;
        gap: 8px;
        margin: 10px 0 8px;
        flex-wrap: wrap;

        .group-tab {
            display: inline-flex;
            align-items: center;
            padding: 4px 12px;
            border: 1px solid #DCDFE6;
            border-radius: 4px;
            background: #fff;
            font-size: 13px;
            color: #606266;
            cursor: pointer;
            user-select: none;
            transition: all 0.2s;

            &:hover {
                color: #409EFF;
                border-color: #409EFF;
            }

            &.is-active {
                color: #409EFF;
                border-color: #409EFF;
                background: #ECF5FF;
                font-weight: 600;
            }

            .group-tag {
                font-style: normal;
                font-size: 10px;
                font-weight: 500;
                padding: 0 4px;
                margin-left: 4px;
                border-radius: 3px;
                line-height: 16px;

                &.is-shared {
                    color: #909399;
                    background: #F2F3F5;
                }

                &.is-custom {
                    color: #409EFF;
                    background: #ECF5FF;
                }
            }
        }
    }

    .dialog-subtitle {
        font-size: 14px;
        font-weight: 600;
        color: #303133;
        margin-bottom: 10px;
    }

    .modal-group-tabs {
        margin: 0 0 14px;
    }

    .form-hint {
        font-size: 12px;
        color: #909399;
        line-height: 1.5;
        margin: 6px 0;
    }

    .sync-checkbox {
        display: flex;
        align-items: center;
        margin-bottom: 12px;
        padding: 8px 10px;
        background: #F5F7FA;
        border-radius: 6px;
    }

    .section-title {
        font-size: 14px;
        font-weight: 600;
        color: #303133;
        margin: 14px 0 8px;

        &:first-of-type {
            margin-top: 0;
        }

        &.summary-section-title {
            margin-top: 16px;
            padding-top: 12px;
            border-top: 1px solid #EBEEF5;
        }
    }

    .answer-mode-group {
        margin-bottom: 10px;
    }

    .week-periods {
        margin-bottom: 12px;
        padding: 10px 12px;
        background: #F5F7FA;
        border: 1px solid #DCDFE6;
        border-radius: 6px;

        .week-periods-label {
            display: block;
            font-size: 12px;
            font-weight: 500;
            color: #909399;
            margin-bottom: 4px;
        }

        .week-periods-text {
            font-size: 13px;
            color: #606266;
            line-height: 1.5;
        }
    }

    .answer-panel {
        margin-bottom: 6px;
    }

    .gsw-answer-fixed-head {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        margin-bottom: 8px;
    }

    .gsw-answer-actions {
        display: flex;
        align-items: center;
        gap: 10px;
    }

    .gsw-answer-selected-count {
        font-size: 12px;
        color: #909399;
    }

    .gsw-answer-calendar.gsw-week-calendar-mode {
        padding: 0;
        border: none;
        background: transparent;
    }

    .gsw-week-calendar {
        display: flex;
        flex-direction: column;
        gap: 12px;
        user-select: none;
        cursor: pointer;

        &.is-dragging {
            cursor: crosshair;
        }
    }

    .gsw-week-block,
    .gsw-min-week-block,
    .gsw-summary-week-item {
        border: 1px solid #EBEEF5;
        border-radius: 8px;
        background: #fff;
        padding: 10px 12px;
    }

    .gsw-week-block-head,
    .gsw-min-week-head {
        display: flex;
        align-items: center;
        gap: 10px;
        margin-bottom: 8px;
        font-size: 13px;
        flex-wrap: wrap;
    }

    .gsw-week-block-label,
    .gsw-min-week-label {
        font-weight: 600;
        color: #303133;
    }

    .gsw-week-block-range,
    .gsw-min-week-range {
        color: #909399;
        font-size: 12px;
    }

    .gsw-min-week-days-count {
        margin-left: auto;
        font-size: 12px;
        color: #909399;
    }

    .gsw-week-days {
        display: flex;
        flex-wrap: wrap;
        gap: 6px;
    }

    .gsw-week-day {
        width: 52px;
        height: 52px;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 2px;
        border: 1px solid transparent;
        border-radius: 4px;
        background: transparent;
        color: #303133;
        cursor: pointer;
        padding: 0;

        &:hover:not(.is-selected) {
            background: #ECF5FF;
            border-color: #B3D8FF;
        }

        &.is-selected {
            background: #059669;
            border-color: #059669;
            color: #fff;

            .gsw-week-day-weekday {
                color: rgba(255, 255, 255, 0.9);
            }
        }

        &.is-in-range:not(.is-selected) {
            background: #D1FAE5;
            border-color: #6EE7B7;
            color: #047857;
        }

        &.is-deselect-preview {
            background: #FEE2E2;
            border-color: #FCA5A5;
            color: #B91C1C;
        }

        &.is-summary {
            box-shadow: inset 0 0 0 2px #D97706;
        }

        &.is-summary.is-selected {
            box-shadow: inset 0 0 0 2px #FCD34D;
        }
    }

    .gsw-week-day-weekday {
        font-size: 11px;
        color: #909399;
        line-height: 1;
    }

    .gsw-week-day-num {
        font-size: 14px;
        font-weight: 600;
        line-height: 1;
    }

    .gsw-week-calendar-empty,
    .gsw-multi-summary-empty {
        padding: 24px;
        text-align: center;
        font-size: 13px;
        color: #909399;
        border: 1px dashed #DCDFE6;
        border-radius: 8px;
        background: #FAFAFA;
    }

    .gsw-min-week-list,
    .gsw-summary-weekly-list,
    .gsw-multi-summary-list {
        display: flex;
        flex-direction: column;
        gap: 10px;
    }

    .gsw-min-week-row,
    .gsw-week-summary-row {
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 13px;
        color: #606266;
    }

    .gsw-week-summary-row {
        margin-top: 10px;
        padding-top: 10px;
        border-top: 1px dashed #EBEEF5;
    }

    .gsw-multi-summary-item .gsw-min-week-head {
        margin-bottom: 0;
    }

    .gsw-multi-range-picks {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        margin-left: auto;
    }

    .gsw-multi-summary-sep {
        font-size: 13px;
        color: #909399;
    }

    .btn-delete-summary {
        color: #F56C6C;
        margin-left: auto;
    }
</style>
